import React, { useState } from "react";

import SquareIcon from "@uui/IntersectSquare";
import LinkIcon from "@uui/Link01";
import { isEmpty } from "lodash/fp";

import { Divider, Typography } from "@mui/material";

import IntegrationChannelSelector from "@app/analytics/components/IntegrationChannelSelector";
import useSlackConversations from "@app/analytics/hooks/integrations/useSlackConversations";
import useSlackIntegration from "@app/analytics/hooks/integrations/useSlackIntegration";
import * as Events from "@app/analytics/MeetingReport/tracking/events";
import type { SlackConversation } from "@app/analytics/types/slackTypes";
import useReportShare, {
  DEFAULT_ACCESS,
} from "@app/common/access/ShareReport/hooks/useReportShare";
import LinkAccess from "@app/common/access/ShareReport/LinkAccess";
import * as ShareUtils from "@app/common/access/ShareReport/utils";
import { toast } from "@app/common/hooks/useToasts";
import ROUTES from "@app/common/routes";
import type {
  ApiEntityAccessView,
  GeneralAccessLevel,
  GeneralAccessPattern,
} from "@app/common/types";

import Alert from "@rui/Alert";
import AppProviderButton from "@rui/AppProviderButton";
import Button from "@rui/Button";
import CopyButton from "@rui/CopyButton";
import {
  EmptyStateContainer,
  EmptyStateIcon,
  EmptyStateSubtext,
  EmptyStateTitle,
} from "@rui/EmptyStateContainer";
import Link from "@rui/Link";
import Modal from "@rui/Modal";
import ModalHeader from "@rui/Modal/ModalHeader";
import { Column, Row } from "@rui/Stack";
import { Bold } from "@rui/styles";
import TooltipWrapper from "@rui/TooltipWrapper";

interface ShareToSlackModalProps {
  sessionId: string;
  workspaceId?: string;
  open: boolean;
  onClose: () => void;
}

const ShareToSlackModal = ({
  sessionId,
  workspaceId,
  open,
  onClose,
}: ShareToSlackModalProps) => {
  const { isConnected } = useSlackIntegration();
  const { conversations, shareSessionToConversation, isLoading, isError } =
    useSlackConversations();

  const { accessList, modifyAccessMutation, modifyGeneralAccessMutation } =
    useReportShare({ sessionId });

  const canShareReport = ShareUtils.canShareReport(
    accessList.currentUserAccessLevel
  );

  const [selectedConversation, setSelectedConversation] =
    useState<SlackConversation | null>(null);

  const handleShareToSlack = () => {
    if (!selectedConversation) return;

    shareSessionToConversation.mutate(
      {
        conversationId: selectedConversation.id,
        sessionId,
      },
      {
        onSuccess: () => {
          Events.postedToSlack();
          toast.success("Your recap has been posted to Slack!");
          onClose();
        },
      }
    );
  };

  const handleLinkAccessChange = (
    generalAccess: GeneralAccessPattern,
    accessLevel: GeneralAccessLevel,
    entitiesToUpdate: ApiEntityAccessView[] = []
  ) => {
    if (!sessionId) return;

    modifyGeneralAccessMutation.mutate(
      {
        generalAccess,
        accessLevel,
        sessionId,
      },
      {
        onError: () => {
          toast.error(
            "There was an error editing access to this report. Please try again or contact support."
          );
        },
      }
    );

    if (!isEmpty(entitiesToUpdate)) {
      entitiesToUpdate.map((entity) => {
        modifyAccessMutation.mutate({
          entity: { ...entity, accessLevel },
          sessionId,
        });
      });
    }
  };

  if (!isLoading && !isConnected) {
    return (
      <Modal
        onClose={onClose}
        open={open}
        sx={{ padding: 0, minWidth: "510px" }}
      >
        <Column padding="16px 24px 12px" spacing="16px">
          <ModalHeader title="Post Recap to Slack" onClose={onClose} />
        </Column>
        <Divider sx={{ borderColor: "shades.neutral.100" }} />
        <Column padding="24px">
          <EmptyStateContainer>
            <EmptyStateIcon Icon={SquareIcon} />
            <EmptyStateTitle>Connect Slack to Read</EmptyStateTitle>
            <EmptyStateSubtext>
              Easily post meeting recaps and share reports to any Slack channel
              in your Workspace.
            </EmptyStateSubtext>
            <AppProviderButton
              component={Link}
              href={ROUTES.CONNECT_SLACK_INTEGRATION}
              provider="slack_bot"
            >
              Connect Slack
            </AppProviderButton>
          </EmptyStateContainer>
        </Column>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose} open={open} sx={{ padding: 0, minWidth: "510px" }}>
      <Column padding="16px 24px 12px" spacing="16px">
        <ModalHeader title="Post Recap to Slack" onClose={onClose} />
      </Column>
      <Divider sx={{ borderColor: "shades.neutral.100" }} />
      <Column
        padding="18px 24px 24px"
        spacing="16px"
        sx={{ maxHeight: "340px", overflowY: "auto" }}
      >
        {!isLoading && isError && (
          <Alert
            severity="error"
            embedded
            alignItems="flex-start"
            sx={{ border: "1px solid #D32F2F" }}
          >
            <Bold>Error</Bold>
            <br />
            There was an error posting to Slack. Please try again.
          </Alert>
        )}
        <IntegrationChannelSelector
          channels={conversations}
          onChange={setSelectedConversation}
          placeholder="Select a Slack channel"
          selected={selectedConversation}
        />
        <Column>
          <Typography
            variant="bodyPrimaryMed"
            color="shades.neutral.800"
            pb={0.5}
          >
            Link access
          </Typography>
          <Alert
            severity="info"
            alignItems="flex-start"
            embedded
            sx={{ color: "shades.neutral.500", mb: "8px" }}
          >
            When posting to Slack, it is recommended to set link access to{" "}
            {workspaceId ? (
              <>
                <Bold>Workspace members</Bold> or{" "}
              </>
            ) : (
              ""
            )}
            <Bold>Anyone with the link</Bold> to ensure that everyone in the
            channel has access.
          </Alert>
          <LinkAccess
            accessList={accessList?.entityAccess ?? []}
            onChange={handleLinkAccessChange}
            generalAccess={
              accessList.generalAccess ?? DEFAULT_ACCESS.generalAccess!
            }
            fullWidth
            hideLabel
            disabled={!canShareReport}
          />
        </Column>
      </Column>
      <Column
        width="100%"
        spacing={1}
        sx={{
          borderTop: (theme) =>
            `1px solid ${theme.palette.shades.neutral[100]}`,
          padding: "16px 24px",
        }}
      >
        <Row variant="apart">
          <CopyButton
            copyValue={ShareUtils.getReportShareLink(sessionId, "share")}
            renderButton={(props, copied) => (
              <Button {...props} startIcon={<LinkIcon />} variant="text">
                {copied ? "Copied!" : "Copy link"}
              </Button>
            )}
          />
          <TooltipWrapper
            title="Select a channel to post to"
            show={!selectedConversation}
          >
            <AppProviderButton
              disabled={!selectedConversation}
              onClick={handleShareToSlack}
              loading={shareSessionToConversation.isPending}
              provider="slack_bot"
            >
              Post to Slack
            </AppProviderButton>
          </TooltipWrapper>
        </Row>
      </Column>
    </Modal>
  );
};

export default ShareToSlackModal;
